import * as React from 'react';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import Skeleton from '@mui/material/Skeleton';
import Avatar, { AvatarProps } from '@mui/material/Avatar';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import * as Device from 'react-device-detect';

import { useSearchMyFriendsLazyQuery, SearchMyFriendsQuery } from '@graphql';
import { truncateText } from '@utils';

interface SearchParticipantsProps {
  participants?: SearchMyFriendsQuery['searchMyFriends'];
  onValidate?: (selected: SearchMyFriendsQuery['searchMyFriends']) => void;
}

export function SearchParticipants(props: Readonly<SearchParticipantsProps>) {
  const { participants = [], onValidate } = props;
  const theme = useTheme();
  const [open, setOpen] = React.useState(false);
  const [selected, setSelected] = React.useState<SearchMyFriendsQuery['searchMyFriends']>(participants);
  const { t } = useTranslation('cta');

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleValidate = () => {
    setOpen(false);

    if (onValidate) {
      onValidate(selected);
    }
  };

  // add near your other handlers
  const handleClickAction = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const target = e.currentTarget as HTMLElement;
    const { friendId, action } = target.dataset;

    if (!friendId?.length) {
      return;
    }

    if (action === 'remove') {
      setSelected((prev) => prev.filter(({ id }) => id !== friendId));
    }
  };

  return (
    <>
      <Button variant="contained" color="uncolored" onClick={handleClickOpen}>
        {!selected.length ? t('add participants') : t('manage participants', { count: selected.length })}
      </Button>
      <Dialog
        open={open}
        onClose={handleClose}
        fullScreen={Device.isMobile}
        sx={{ '& .MuiDialog-paper': { height: Device.isMobile ? undefined : '90dvh' } }}
      >
        <DialogTitle>Participants</DialogTitle>
        <DialogContent dividers>
          <DialogContentText>
            Ajoute des particpants à ton événement en utilisant la recherche.
          </DialogContentText>
          <SearchFriends selected={selected} setSelected={setSelected} />
          {selected.map((friend) => (
            <Stack
              key={friend.id}
              direction="row"
              spacing={1}
              alignItems="center"
              width="100%"
              divider={<Divider />}
            >
              <Avatar
                src={friend.avatar?.url}
                alt={friend.name}
                sx={{ width: { xs: 30, sm: 50 }, height: { xs: 30, sm: 50 } }}
              />
              <Stack width="100%">
                <Typography fontWeight="bold" fontSize={{ xs: '.8rem', sm: '.9rem' }}>
                  {truncateText(friend.name, 25)}
                </Typography>
                <Typography color="textSecondary" fontSize={{ xs: '.7rem', sm: '.8rem' }}>
                  @{truncateText(friend.slug, 25)}
                </Typography>
              </Stack>
              <Stack spacing={2} direction="row" alignItems="center">
                <IconButton
                  data-friend-id={friend.id}
                  data-action="remove"
                  onClick={handleClickAction}
                  sx={{
                    width: { xs: 30, sm: 40 },
                    height: { xs: 30, sm: 40 },
                    backgroundColor: '#424242',
                    '&:hover': { backgroundColor: 'darkred' },
                  }}
                >
                  <theme.icons.close
                    sx={{
                      color: 'lightgrey',
                      '&:hover': { color: 'white' },
                      width: { xs: 20, sm: 30 },
                      height: { xs: 20, sm: 30 },
                    }}
                  />
                </IconButton>
              </Stack>
            </Stack>
          ))}
        </DialogContent>
        <DialogActions>
          <Button fullWidth variant="contained" color="uncolored" onClick={handleValidate}>
            {t('close')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

interface SearchFriendsProps {
  selected: SearchMyFriendsQuery['searchMyFriends'];
  setSelected: React.Dispatch<React.SetStateAction<SearchMyFriendsQuery['searchMyFriends']>>;
}

function SearchFriends(props: Readonly<SearchFriendsProps>) {
  const { selected, setSelected } = props;
  const [options, setOptions] = React.useState<SearchMyFriendsQuery['searchMyFriends']>([]);
  const [inputValue, setInputValue] = React.useState('');
  const { t } = useTranslation('cta');

  const [searchMyFriends, { loading }] = useSearchMyFriendsLazyQuery({
    onCompleted: ({ searchMyFriends }) => {
      setOptions(searchMyFriends);
    },
  });

  const handleSearch = React.useMemo(
    () =>
      _.debounce((searchValue: string) => {
        if (!searchValue.trim()) {
          setOptions([]);
          return;
        }

        searchMyFriends({ variables: { text: searchValue } });
      }, 300),
    [searchMyFriends]
  );

  React.useEffect(() => {
    handleSearch(inputValue);

    return () => {
      handleSearch.cancel();
    };
  }, [inputValue, handleSearch]);

  return (
    <Autocomplete
      sx={{ my: 5 }}
      multiple
      fullWidth
      autoFocus
      disableClearable
      options={options}
      value={selected}
      inputValue={inputValue}
      loading={loading}
      filterOptions={(x) => x}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      getOptionLabel={(option) => option.name}
      disableCloseOnSelect
      onChange={(_, newValue) => {
        setSelected(newValue);
      }}
      onInputChange={(_, newInputValue, reason) => {
        // prevent MUI from clearing the input when selecting/deselecting an option
        if (reason === 'removeOption') return;
        if (reason === 'selectOption') return;

        setInputValue(newInputValue);

        if (!newInputValue.trim()) {
          setOptions([]);
        }
      }}
      renderOption={(props, option, { selected }) => {
        const { key, ...optionProps } = props;
        const SelectionIcon = selected ? CheckBoxIcon : CheckBoxOutlineBlankIcon;

        return (
          <li key={key} {...optionProps}>
            <Stack direction="row" spacing={1} alignItems="center" width="100%" divider={<Divider />}>
              <AvatarWithSkeleton
                src={option.avatar?.url}
                alt={option.name}
                sx={{ width: { xs: 30, sm: 50 }, height: { xs: 30, sm: 50 } }}
              />
              <Stack width="100%">
                <Typography fontWeight="bold" fontSize={{ xs: '.8rem', sm: '.9rem' }}>
                  {truncateText(option.name, 25)}
                </Typography>
                <Typography color="textSecondary" fontSize={{ xs: '.7rem', sm: '.8rem' }}>
                  @{truncateText(option.slug, 25)}
                </Typography>
              </Stack>
            </Stack>
            <SelectionIcon
              fontSize="small"
              style={{ marginRight: 8, padding: 9, boxSizing: 'content-box' }}
            />
          </li>
        );
      }}
      renderTags={() => null}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="standard"
          placeholder={t('search', { ns: 'cta' })}
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: (
                <React.Fragment>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </React.Fragment>
              ),
            },
          }}
        />
      )}
    />
  );
}

interface AvatarWithSkeletonProps {
  src?: string | null;
}

function AvatarWithSkeleton(props: Readonly<AvatarWithSkeletonProps & AvatarProps>) {
  const [loaded, setLoaded] = React.useState(false);
  const { src, alt, sx, ...otherProps } = props;

  return (
    <Box position="relative" sx={sx}>
      {!loaded && <Skeleton variant="circular" sx={sx} animation="wave" />}

      <Avatar
        src={src ?? undefined}
        alt={alt}
        imgProps={{
          onLoad: () => setLoaded(true),
          onError: () => setLoaded(true),
        }}
        sx={{
          ...sx,
          position: 'absolute',
          top: 0,
          left: 0,
          opacity: loaded ? 1 : 0,
        }}
      />
    </Box>
  );
}

export default React.memo(SearchParticipants);
